import type React from 'react';
import { useCallback, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { deleteBot, getBot } from '../api/admin';
import { useAsync } from '../api/useAsync';
import { AsyncBoundary, ConfirmDialog, Pill } from '../components/ui';
import { BotProfileForm } from './bot/BotProfileForm';
import { BotCardsPanel } from './bot/BotCardsPanel';
import { BotDeckEditor } from './bot/BotDeckEditor';

export function BotDetailPage(): React.JSX.Element {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const load = useCallback(() => getBot(id), [id]);
  const { data, loading, error, reload } = useAsync(load, [id]);

  const [confirming, setConfirming] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const onDelete = async (): Promise<void> => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteBot(id);
      navigate('/bots', { replace: true });
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Não foi possível excluir o bot.');
      setDeleting(false);
      setConfirming(false);
    }
  };

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <Link to="/bots" className="back-link">
            ← Bots
          </Link>
          <h1>{data?.bot.displayName ?? 'Bot'}</h1>
          {data ? (
            <p className="page__subtitle">
              @{data.bot.userName} · nível {data.bot.level ?? '—'}{' '}
              {data.bot.status !== 'active' ? <Pill tone="danger">{data.bot.status}</Pill> : null}
            </p>
          ) : null}
        </div>
        {data ? (
          <button type="button" className="btn btn--danger" onClick={() => setConfirming(true)}>
            Excluir bot
          </button>
        ) : null}
      </header>

      {deleteError ? <div className="banner banner--error">{deleteError}</div> : null}

      <AsyncBoundary loading={loading && !data} error={error} onRetry={reload}>
        {data ? (
          <>
            {data.degraded.length > 0 ? (
              <div className="banner banner--warn">
                <div>
                  Não foi possível carregar: {data.degraded.join(', ')} — o servidor de
                  inventário não respondeu.
                </div>
                {/* Show the upstream reason: the browser only ever sees a 200
                    here (o BFF degrada de propósito), so sem isto não há como
                    diagnosticar. */}
                {data.degradedReasons
                  ? Object.entries(data.degradedReasons).map(([field, reason]) => (
                      <div key={field} className="banner__detail">
                        <code>
                          {field}: {reason}
                        </code>
                      </div>
                    ))
                  : null}
              </div>
            ) : null}

            <div className="bot-layout">
              <BotProfileForm bot={data.bot} onSaved={reload} />
              <BotDeckEditor
                botId={id}
                decks={data.decks}
                cards={data.cards}
                onChanged={reload}
              />
            </div>

            <BotCardsPanel botId={id} cards={data.cards} onChanged={reload} />
          </>
        ) : null}
      </AsyncBoundary>

      {confirming && data ? (
        <ConfirmDialog
          title="Excluir bot"
          confirmLabel="Excluir definitivamente"
          confirmPhrase={data.bot.userName}
          busy={deleting}
          message={
            <>
              <p>
                Isso remove <strong>{data.bot.displayName}</strong>, todas as suas cartas e
                todos os seus decks. A ação não pode ser desfeita.
              </p>
              <p>O bot sai imediatamente do matchmaking.</p>
            </>
          }
          onConfirm={() => void onDelete()}
          onCancel={() => setConfirming(false)}
        />
      ) : null}
    </div>
  );
}
